import { loadSkinToCanvas } from 'skinview-utils'
import { createCanvas, loadImageFromUrl } from '../utils'

export { default as stevePngUrl } from 'mc-assets/dist/other-textures/latest/entity/player/wide/steve.png'

const config = {
  apiEnabled: true,
  skinApiUrl: '' as string,
}

export const setSkinsConfig = (newConfig: Partial<typeof config>) => {
  Object.assign(config, newConfig)
}

const sha1Hex = async (value: string) => {
  const subtleCrypto = globalThis.crypto?.subtle
  if (!subtleCrypto) throw new Error('crypto.subtle is not available')

  const encoded = new TextEncoder().encode(value)
  const digest = await subtleCrypto.digest('SHA-1', encoded)
  return Array.from(new Uint8Array(digest), byte => byte.toString(16).padStart(2, '0')).join('')
}

const buildCustomSkinApiUrl = async (username: string) => {
  const baseUrl = config.skinApiUrl.trim()

  // Backward compatibility for old query format
  if (baseUrl.includes('{username}')) {
    return baseUrl.replace('{username}', encodeURIComponent(username))
  }

  // New format requested by backend: <aa>/<bb>/<full-sha1>.json
  const hash = await sha1Hex(username)
  const normalizedBase = baseUrl.replace(/\/+$/, '')
  return `${normalizedBase}/${hash.slice(0, 2)}/${hash.slice(2, 4)}/${hash}.json`
}

export async function loadSkinFromUsername (username: string, type: 'skin' | 'cape'): Promise<string | undefined> {
  if (!config.apiEnabled) return

  // Use custom skin API endpoint if configured
  if (config.skinApiUrl) {
    try {
      const url = await buildCustomSkinApiUrl(username)
      const response = await fetch(url)
      if (!response.ok) return
      const data: {
        textures?: {
          SKIN?: { url: string }
          CAPE?: { url: string }
        }
      } = await response.json()
      if (type === 'cape') return data.textures?.CAPE?.url
      return data.textures?.SKIN?.url
    } catch {
      return
    }
  }

  if (type === 'cape') return
  const url = `https://playerdb.co/api/player/minecraft/${username}`
  const response = await fetch(url)
  if (!response.ok) return

  const data: {
    data: {
      player: {
        skin_texture: string
      }
    }
  } = await response.json()
  return data.data.player.skin_texture
}

export const parseSkinTexturesValue = (value: string) => {
  const decodedData: {
    textures: {
      SKIN: {
        url: string
      }
    }
  } = JSON.parse(Buffer.from(value, 'base64').toString())
  return decodedData.textures?.SKIN?.url
}

export async function loadSkinImage (skinUrl: string): Promise<{ canvas: OffscreenCanvas, image: ImageBitmap }> {
  if (!skinUrl.startsWith('data:')) {
    skinUrl = await fetchAndConvertBase64Skin(skinUrl.replace('http://', 'https://'))
  }

  const image = await loadImageFromUrl(skinUrl)
  const skinCanvas = createCanvas(64, 64)
  loadSkinToCanvas(skinCanvas, image)
  return { canvas: skinCanvas, image }
}

const fetchAndConvertBase64Skin = async (skinUrl: string) => {
  const response = await fetch(skinUrl, { })
  const arrayBuffer = await response.arrayBuffer()
  const base64 = Buffer.from(arrayBuffer).toString('base64')
  return `data:image/png;base64,${base64}`
}
